import React, { useEffect, useRef } from 'react';

import { BaseCheckbox } from 'src/components/BaseCheckbox';
import { BaseCounter } from 'src/components/BaseCounter';
import { BaseDateRangePicker } from 'src/components/BaseDateRangePicker';
import { BaseFileUpload } from 'src/components/BaseFileUpload';
import { BaseInput } from 'src/components/BaseInput';
import { BaseSelect } from 'src/components/BaseSelect';
import { BaseSlider } from 'src/components/BaseSlider';
import { BaseSwitch } from 'src/components/BaseSwitch';
import { BaseTextarea } from 'src/components/BaseTextarea';
import { FieldContainer } from 'src/components/FormFieldsGenerator/components/FieldContainer';
import { InfoHelper } from '../InfoHelper';
import { LocationSearch } from 'src/components/LocationSearch';
import { TFormFieldsGeneratorProps } from './FormFieldsGenerator.types';
import { UTCMoment } from '../../utils/UTCMoment';
import classnames from 'classnames';
import { convertLngLat } from 'src/utils/worldMap/helpers';
import s from './FormFieldsGenerator.module.scss';
import vars from 'src/assets/styles/utils/vars.module.scss';

export const FormFieldsGenerator: React.FC<TFormFieldsGeneratorProps> = ({
  className,
  style,
  fields,
  errors,
  onSubmit,
  onChange,
  onBlur,
  onLocationChange,
  id,
  focusField,
  theme,
  flyAfterLocationSelect,
  disableLocationField,
  readOnly,
}) => {
  const containerRef = useRef<HTMLFormElement>(null);

  const handleSubmit: React.DOMAttributes<HTMLFormElement>['onSubmit'] = (e) => {
    e.preventDefault();

    if (readOnly) return;

    onSubmit();
  };

  useEffect(() => {
    setTimeout(() => {
      if (focusField && containerRef.current) {
        const el:
          | HTMLInputElement
          | HTMLTextAreaElement
          | null = containerRef.current.querySelector(
          `input[name="${focusField}"], textarea[name="${focusField}"]`,
        );

        el?.focus();
      }
    });
  }, [focusField]);

  return (
    <form
      style={style}
      className={classnames(s.container, className)}
      onSubmit={handleSubmit}
      id={id}
      ref={containerRef}>
      {fields.map((f, index) => {
        const containerProps = {
          inlineAlign: f.inlineAlign,
          showTooltip: f.showTooltip,
          tooltipText: f.tooltipText,
          key: f.name,
        };
        const fieldProps = {
          disabled: f.disabled || readOnly,
          error: errors?.[f.name],
          label: f.label,
          name: f.name,
          onChange,
        };
        const errorWidth = f.fullWidthErr && f.inlineAlign ? '200%' : undefined;
        const marginTopSmall =
          (fields[index - 1] || {}).type === 'switcher' ||
          ((fields[index - 2] || {}).type === 'switcher' && (fields[index - 1] || {}).inlineAlign);

        switch (f.type) {
          case 'text':
            return (
              <FieldContainer
                {...containerProps}
                className={classnames(s.inputField, {
                  [s.marginTopSmall]: marginTopSmall,
                })}>
                <BaseInput
                  {...fieldProps}
                  type="text"
                  theme={theme === 'dark' ? 'line-dark' : 'line-light'}
                  value={f.value}
                  errorWidth={errorWidth}
                  autoComplete="off"
                  onBlur={onBlur}
                />
              </FieldContainer>
            );

          case 'textarea':
            return (
              <FieldContainer
                {...containerProps}
                className={classnames(s.inputField, {
                  [s.marginTopSmall]: marginTopSmall,
                })}>
                <BaseTextarea
                  {...fieldProps}
                  theme={theme === 'dark' ? 'line-dark' : 'line-light'}
                  inputHeight="2"
                  value={f.value}
                  errorWidth={errorWidth}
                  onBlur={onBlur}
                />
              </FieldContainer>
            );

          case 'number':
            return (
              <FieldContainer
                {...containerProps}
                className={classnames(s.inputField, {
                  [s.marginTopSmall]: marginTopSmall,
                })}>
                <BaseInput
                  {...fieldProps}
                  type="number"
                  theme={theme === 'dark' ? 'line-dark' : 'line-light'}
                  value={f.value}
                  unit={f.unit}
                  errorWidth={errorWidth}
                  onBlur={onBlur}
                />
              </FieldContainer>
            );

          case 'switcher':
            return (
              <FieldContainer {...containerProps} className={s.switchWrapper}>
                <BaseSwitch
                  {...fieldProps}
                  className={s.switch}
                  value={f.value}
                  options={f.options}
                  theme={theme === 'dark' ? 'light' : 'gradient-dark'}
                  variant="horizontal-edge"
                />
              </FieldContainer>
            );

          case 'location':
            return (
              <FieldContainer {...containerProps} className={s.locationField}>
                <LocationSearch
                  {...fieldProps}
                  disabled={disableLocationField || f.disabled || readOnly}
                  value={f.value}
                  onChange={({ name, ...rest }) => {
                    onChange({ name, value: convertLngLat(rest) });
                    onLocationChange?.({ name, ...rest });
                  }}
                  flyAfterSelect={flyAfterLocationSelect}
                  theme="line-light"
                  iconLeft={undefined}
                  iconRight={'locate'}
                  iconRightColor={vars['color-oh-so-green']}
                  elevateLabel={true}
                  dropdownClassName={s.locationSearchDropdown}
                  onBlur={onBlur}
                />
              </FieldContainer>
            );

          case 'enum':
            return (
              <FieldContainer {...containerProps} className={s.selectField}>
                <BaseSelect
                  {...fieldProps}
                  value={f.value}
                  options={f.options}
                  theme={theme === 'dark' ? 'line-dark' : 'line-light'}
                />
              </FieldContainer>
            );

          case 'slider':
            return (
              <FieldContainer {...containerProps} className={s.sliderField}>
                <BaseSlider
                  {...fieldProps}
                  value={f.value}
                  min={f.minVal}
                  max={f.maxVal}
                  step={f.step}
                  label={`${f.label}: ${Array.isArray(f.value) ? f.value.join('–') : f.value} ${
                    f.unit || ''
                  }`}
                  showButtons={false}
                />
              </FieldContainer>
            );

          case 'file':
            return (
              <FieldContainer {...containerProps} className={s.fileUploadField}>
                <BaseFileUpload
                  {...fieldProps}
                  value={f.value}
                  theme="line-light"
                  allowDownload={f.allowDownload}
                />
              </FieldContainer>
            );

          case 'checkbox':
            return (
              <FieldContainer {...containerProps} className={s.checkboxField}>
                <BaseCheckbox {...fieldProps} value={f.value} theme="gradient-dark" />
              </FieldContainer>
            );

          case 'counter':
            return (
              <FieldContainer {...containerProps} className={s.counter}>
                <BaseCounter {...fieldProps} value={f.value} min={f.min} max={f.max} />
              </FieldContainer>
            );

          case 'dateRange':
            return (
              <FieldContainer {...containerProps} className={s.datePickerContainer}>
                <div className={s.lengthWrapper}>
                  <div className={s.lengthText}>Simulation Length</div>
                  <InfoHelper
                    className={s.lengthTooltip}
                    info="Choose the dates for the run time of the simulation below. Simulations can run from 1 day up to 1 month."
                  />
                </div>
                <BaseDateRangePicker
                  {...fieldProps}
                  theme={theme === 'dark' ? 'light' : 'dark'}
                  startValue={f.value.startDate}
                  endValue={f.value.endDate}
                  valueFormat={f.valueFormat}
                  minDate={UTCMoment.utc(f.value.startDate).add(1, 'day').toDate()}
                  maxDate={UTCMoment.utc(f.value.endDate).subtract(1, 'day').toDate()}
                  onChange={({ startDate, endDate }) => {
                    onChange({
                      name: f.name,
                      value: { startDate: startDate, endDate: endDate },
                    });
                  }}
                  tags={[
                    {
                      value: 7,
                      label: '7 Days',
                      badge: 'Recommended',
                    },
                    {
                      value: 14,
                      label: '2 Weeks',
                    },
                    {
                      value: 30,
                      label: '1 Month',
                    },
                  ]}
                />
              </FieldContainer>
            );

          default:
            break;
        }
      })}
    </form>
  );
};
